import { getBoardRequiredColumns, getIntegrationConfigData } from "frontend/api";
import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { useFeatureFlag } from "./use-feature-flag/use-feature-flag";
import * as R from "rambda";
import useSWR, { SWRResponse } from "swr";
import { MondayBoard } from "shared/integrations/mapper";

const boardsAtom = atomFamily((_integrationId: string) => atom<MondayBoard[]>([]));
const getSubItemBoardId = R.path<string>(["subitem_board", "id"]);

/**
 * Return a list of monday boards accessible to the integration.
 * @param integrationId The integration id to fetch boards for.
 * @param searchTerm The search term to filter boards by name.
 * @param includeSubitems If true, will include subitems boards in the list returned.
 * @returns List of boards, loading state, and error state.
 */
export default function useMondayBoardsPicker(
  integrationId: string,
  searchTerm: string = "",
  includeSubitems: boolean = false
) {
  const [allBoards, setAllBoards] = useAtom(boardsAtom(`${integrationId}${includeSubitems}`));
  const [boards, setBoards] = useState<MondayBoard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [failedToLoad, setFailedToLoad] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const shouldExcludeRestrictedColumns = useFeatureFlag("restricted-columns");

  const filteredBoards = useMemo(() => {
    if (!debouncedSearchTerm) {
      return allBoards;
    }
    if (boards.length > 0) {
      return boards;
    }
    return allBoards.filter((board) => board.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
  }, [debouncedSearchTerm, boards, allBoards]);

  useEffect(() => {
    if (allBoards.length) {
      return;
    }
    fetchBoards("", includeSubitems);
  }, [includeSubitems]);

  useEffect(() => {
    if (!debouncedSearchTerm) {
      return;
    }
    fetchBoards(debouncedSearchTerm, includeSubitems);
  }, [debouncedSearchTerm, includeSubitems]);

  async function fetchBoards(searchTerm: string, includeSubitems: boolean) {
    setIsLoading(true);
    setFailedToLoad(false);
    setBoards([]);
    try {
      let { boards } = await getIntegrationConfigData(integrationId, [], {
        searchTerm,
        shouldExcludeRestrictedColumns,
      });
      if (includeSubitems) {
        const boardsWithSubItems = boards.filter(getSubItemBoardId);
        const justTheIds = boardsWithSubItems.map(getSubItemBoardId);

        const { boards: subitemBoards } = await getIntegrationConfigData(integrationId, justTheIds, {
          shouldExcludeRestrictedColumns,
        });
        //subitemBoards should now be an array of boards, but maybe some entries are missing
        //would be useful if backend returned them in the same order, and put null/undefined for missing entries.
        //then matching the data will be O(n) instead of O(n^2).
        boards = boardsWithSubItems
          .map((board: MondayBoard) => {
            const subitemBoardId: string = getSubItemBoardId(board)!;
            const subitemBoard = subitemBoards.find((subitemBoard: MondayBoard) => subitemBoard.id === subitemBoardId);
            if (subitemBoard) {
              return { ...board, subitem_board: subitemBoard };
            }
            return null;
          })
          .filter(Boolean);
      }
      if (searchTerm) {
        setBoards(boards);
      } else {
        setAllBoards(boards);
      }
    } catch (e) {
      setFailedToLoad(true);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  return { boards: filteredBoards, isLoading, failedToLoad };
}

/**
 * Return a list of monday boards accessible to the integration.
 * @param integrationId
 * @param searchTerm
 * @param includeSubitems
 * @returns {data, error, isLoading, isValidating}
 */
export function useMondayBoards(
  integrationId: string,
  searchTerm: string = "",
  includeSubitems: boolean = false
): SWRResponse<MondayBoard[], Error> {
  const shouldExcludeRestrictedColumns = useFeatureFlag("restricted-columns");
  return useSWR(
    [integrationId, searchTerm, includeSubitems, shouldExcludeRestrictedColumns],
    async ([id, searchTerm, includeSubitems, shouldExcludeRestrictedColumns]) => {
      let { boards } = await getIntegrationConfigData(id, [], {
        searchTerm,
        shouldExcludeRestrictedColumns,
      });
      if (includeSubitems) {
        const boardsWithSubItems = boards.filter(getSubItemBoardId);
        const justTheIds = boardsWithSubItems.map(getSubItemBoardId);
        const { boards: subitemBoards } = await getIntegrationConfigData(id, justTheIds, {
          shouldExcludeRestrictedColumns,
        });
        //subitemBoards should now be an array of boards, but maybe some entries are missing
        //would be useful if backend returned them in the same order, and put null/undefined for missing entries.
        //then matching the data will be O(n) instead of O(n^2).
        boards = boardsWithSubItems
          .map((board: MondayBoard) => {
            const subitemBoardId: string = getSubItemBoardId(board)!;
            const subitemBoard = subitemBoards.find((subitemBoard: MondayBoard) => subitemBoard.id === subitemBoardId);
            if (subitemBoard) {
              return { ...board, subitem_board: subitemBoard };
            }
            return null;
          })
          .filter(Boolean);
      }
      return boards;
    },
    {
      revalidateIfStale: false, // if this is true, it will revalidate everytime the component is mounted
      keepPreviousData: true, // according to docs, this helps with fetching with search-queries
    }
  );
}

export function useBoardRequiredColumns(integrationId: string, boardId: string) {
  return useSWR(
    [integrationId, boardId],
    async ([id, boardId]) => {
      if (!boardId || !id) {
        return null;
      }
      const columns = await getBoardRequiredColumns(id, boardId);
      return columns;
    },
    {
      revalidateIfStale: false,
      keepPreviousData: true,
    }
  );
}
